import React, {useState} from 'react';
import './App.scss';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Switch as SwitchComponent,
    ThemeProvider,
    Toolbar,
    Typography,
    createTheme,
    useMediaQuery,
    useTheme,
    Theme
} from '@mui/material';
import {
    Menu as MenuIcon,
    Home as HomeIcon,
    Info as InfoIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Science as ScienceIcon,
    Psychology as PsychologyIcon,
    BarChart as BarChartIcon,
    Timeline as TimelineIcon,
    DataObject as DataObjectIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import {BrowserRouter, Link} from "react-router-dom";
import {Home} from "./home/Home";
import {About} from "./about/About";
import {Contact} from "./contact/Contact";
import {Privacy} from "./privacy/Privacy";
import {DataScience} from "./data-science/DataScience";
import {MachineLearning} from "./machine-learning/MachineLearning";

function App() {
    const [darkTheme, setDarkTheme] = useState(true);
    const [isOpen, setOpen] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState('home');
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const theme = createTheme({
        palette: {
            mode: darkTheme ? 'dark' : 'light',
        },
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
            },
        },
    });

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const drawerWidth = isMobile ? 280 : 320;


    const dataScienceTopics = [
        { id: 'statistics', label: 'Statistics', icon: <BarChartIcon /> },
        { id: 'data_visualization', label: 'Data Visualization', icon: <TimelineIcon /> },
        { id: 'data_preprocessing', label: 'Data Preprocessing', icon: <DataObjectIcon /> },
        { id: 'exploratory_data_analysis', label: 'Exploratory Data Analysis', icon: <AnalyticsIcon /> },
    ];

    const mlTopics = [
        { id: 'supervised_learning', label: 'Supervised Learning', icon: <ScienceIcon /> },
        { id: 'unsupervised_learning', label: 'Unsupervised Learning', icon: <PsychologyIcon /> },
        { id: 'deep_learning', label: 'Deep Learning', icon: <ScienceIcon /> },
        { id: 'model_evaluation', label: 'Model Evaluation', icon: <AnalyticsIcon /> },
    ];

    const handleTopicSelect = (topicId: string) => {
        setSelectedTopic(topicId);
        setOpen(false);
    };

    const renderContent = () => {
        switch (selectedTopic) {
            case 'home':
                return <Home />;
            case 'about':
                return <About />;
            case 'contact':
                return <Contact />;
            case 'privacy':
                return <Privacy />;
            // Data Science topics
            case 'statistics':
            case 'data_visualization':
            case 'data_preprocessing':
            case 'exploratory_data_analysis':
                return <DataScience />;
            // Machine Learning topics
            case 'supervised_learning':
            case 'unsupervised_learning':
            case 'deep_learning':
            case 'model_evaluation':
                return <MachineLearning />;
            default:
                return <Home />;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <Box sx={{ display: 'flex' }}>
                        <AppBar 
                            position="fixed" 
                            sx={{ 
                                zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
                                background: (theme: Theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)'
                                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.98) 0%, rgba(118, 75, 162, 0.98) 100%)',
                                boxShadow: 2,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'20\' height=\'20\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 20 0 L 0 0 0 20\' fill=\'none\' stroke=\'rgba(255,255,255,0.05)\' stroke-width=\'1\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\' /%3E%3C/svg%3E")',
                                    opacity: 0.3,
                                    pointerEvents: 'none'
                                }
                            }}
                        >
                            <Toolbar sx={{ position: 'relative', zIndex: 1 }}>
                                <IconButton
                                    color="inherit"
                                    aria-label="open drawer"
                                    onClick={handleOpen}
                                    edge="start"
                                    sx={{ 
                                        mr: 2,
                                        '&:hover': {
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            transform: 'scale(1.1)',
                                            transition: 'all 0.2s'
                                        }
                                    }}
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    mr: 2,
                                    pr: 2,
                                    borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <Box sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        animation: 'pulse 2s ease-in-out infinite',
                                        '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                            '50%': { opacity: 0.5, transform: 'scale(1.2)' }
                                        }
                                    }} />
                                </Box>
                                <Typography 
                                    variant={isMobile ? "h6" : "h5"} 
                                    noWrap 
                                    component="div" 
                                    sx={{ 
                                        flexGrow: 1,
                                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                        fontWeight: 'bold',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {isMobile ? 'Meeraj K.' : 'Meeraj Kanaparthi'}
                                </Typography>
                                
                                {/* Desktop Navigation */}
                                {!isMobile && (
                                    <>
                                        <Button color="inherit" component={Link} to="/" sx={{ mr: 1 }}>
                                            <HomeIcon sx={{ mr: 1 }} />
                                            Home
                                        </Button>
                                        <IconButton color="inherit" component={Link} to="/about" sx={{ mr: 1 }}>
                                            <InfoIcon />
                                        </IconButton>
                                        <Button color="inherit" disabled sx={{ mr: 1 }}>
                                            Login
                                        </Button>
                                        <Button color="inherit" disabled sx={{ mr: 1 }}>
                                            Register
                                        </Button>
                                    </>
                                )}
                                
                                {/* Theme Toggle */}
                                <SwitchComponent
                                    checked={darkTheme}
                                    onChange={() => setDarkTheme(!darkTheme)}
                                    icon={<LightModeIcon />}
                                    checkedIcon={<DarkModeIcon />}
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Toolbar>
                        </AppBar>

                    <Drawer
                        variant={isMobile ? "temporary" : "persistent"}
                        open={isOpen}
                        onClose={handleClose}
                        sx={{
                            width: drawerWidth,
                            flexShrink: 0,
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                boxSizing: 'border-box',
                                top: isMobile ? 0 : 64, // Account for AppBar height on desktop
                                height: isMobile ? '100%' : 'calc(100% - 64px)',
                            },
                        }}
                    >
                        <Toolbar sx={{ minHeight: isMobile ? 56 : 0 }} />
                        <Box sx={{ overflow: 'auto', px: 1 }}>
                            <List>
                                {/* Main Navigation */}
                                <ListItem disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleTopicSelect('home')}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <ListItemIcon>
                                            <HomeIcon />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Home" 
                                            primaryTypographyProps={{ 
                                                fontSize: isMobile ? '0.9rem' : '1rem' 
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleTopicSelect('about')}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <ListItemIcon>
                                            <InfoIcon />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="About" 
                                            primaryTypographyProps={{ 
                                                fontSize: isMobile ? '0.9rem' : '1rem' 
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleTopicSelect('contact')}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <ListItemIcon>
                                            <InfoIcon />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Contact" 
                                            primaryTypographyProps={{ 
                                                fontSize: isMobile ? '0.9rem' : '1rem' 
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleTopicSelect('privacy')}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <ListItemIcon>
                                            <InfoIcon />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Privacy" 
                                            primaryTypographyProps={{ 
                                                fontSize: isMobile ? '0.9rem' : '1rem' 
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>

                                <Divider sx={{ my: 2 }} />

                                {/* Data Science Section */}
                                <ListItem>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'primary.main',
                                            fontSize: isMobile ? '1rem' : '1.1rem'
                                        }}
                                    >
                                        Data Science
                                    </Typography>
                                </ListItem>
                                {dataScienceTopics.map((topic) => (
                                    <ListItem key={topic.id} disablePadding>
                                        <ListItemButton 
                                            onClick={() => handleTopicSelect(topic.id)}
                                            sx={{ borderRadius: 1, mb: 0.5 }}
                                        >
                                            <ListItemIcon>
                                                {topic.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={topic.label} 
                                                primaryTypographyProps={{ 
                                                    fontSize: isMobile ? '0.9rem' : '1rem' 
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}

                                <Divider sx={{ my: 2 }} />

                                {/* Machine Learning Section */}
                                <ListItem>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'primary.main',
                                            fontSize: isMobile ? '1rem' : '1.1rem'
                                        }}
                                    >
                                        Machine Learning
                                    </Typography>
                                </ListItem>
                                {mlTopics.map((topic) => (
                                    <ListItem key={topic.id} disablePadding>
                                        <ListItemButton 
                                            onClick={() => handleTopicSelect(topic.id)}
                                            sx={{ borderRadius: 1, mb: 0.5 }}
                                        >
                                            <ListItemIcon>
                                                {topic.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={topic.label} 
                                                primaryTypographyProps={{ 
                                                    fontSize: isMobile ? '0.9rem' : '1rem' 
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Drawer>

                    <Box 
                        component="main" 
                        sx={{ 
                            flexGrow: 1, 
                            p: { xs: 1, sm: 2, md: 3 },
                            ml: { xs: 0, md: isOpen ? `${drawerWidth}px` : 0 },
                            transition: 'margin 0.3s ease',
                            minHeight: '100vh',
                            mt: { xs: '64px', sm: '64px' } // Margin top to account for fixed AppBar
                        }}
                    >
                        <Container 
                            maxWidth="lg" 
                            sx={{ 
                                px: { xs: 1, sm: 2 },
                                py: { xs: 1, sm: 2 }
                            }}
                        >
                            <Box sx={{ mb: 2 }}>
                                {renderContent()}
                            </Box>
                        </Container>
                    </Box>
                </Box>

                <Divider />
                <Box 
                    component="footer" 
                    sx={{ 
                        py: { xs: 1, sm: 2 }, 
                        px: { xs: 1, sm: 2 }, 
                        mt: 'auto', 
                        backgroundColor: 'grey.100',
                        minHeight: { xs: 60, sm: 80 }
                    }}
                >
                    <Container maxWidth="lg">
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                gap: { xs: 1, sm: 2 }
                            }}
                        >
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}
                            >
                                Copyright © 2025, Meeraj Kanaparthi, Hyderabad, India. All rights reserved
                            </Typography>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    gap: { xs: 1, sm: 2 },
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'center', sm: 'flex-end' }
                                }}
                            >
                                <Button 
                                    component={Link} 
                                    to="/privacy" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                >
                                    Privacy Policy
                                </Button>
                                <Button 
                                    component={Link} 
                                    to="/contact" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                >
                                    Contact Us
                                </Button>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
